import moment, { type Duration, type Moment } from "moment";
import { getOption } from "../Platform/Options/Options";

/**
 * Uppercase the first letter in the string
 */
export function uppercaseFirst<T extends string>(string: T): Capitalize<T> {
    return string.charAt(0).toUpperCase() + string.slice(1) as Capitalize<T>;
}

/**
 * Make a word a plural based of the provided count
 */
export function pluralize(string: string, count: number): string {
    if(count === 1) {
        return string;
    }

    if(string.slice(-1) === "s") {
        return string + "'";
    }

    if(string.slice(-1) === "y") {
        return string.slice(0, -1) + "ies";
    }

    return string + "s";
}

/**
 * Converts a noun into a possessive noun
 */
export function possessivize(string: string): string {
    const trimmed = string.trimEnd();

    if(trimmed.charAt(trimmed.length - 1) === "s") {
        return trimmed + "'";
    }

    return trimmed + "'s";
}

const VOWELS = [ "a", "e", "i", "o", "u" ];

/**
 * Prepends "an" or "a" to the start of the string based on the provided string starting with a vowel
 * If the provided count is greater than one, then the number is prepended instead
 * Note: This doesn't handle the edge cases of starting with "h"
 */
export function addIndefiniteArticle(string: string, count?: number): string {
    if(typeof count === "number" && count > 1) {
        return `${count} ${string}`;
    } else if(!string.length) {
        return string;
    }

    const startsWithVowel = VOWELS.includes(string[0].toLowerCase());

    return `a${startsWithVowel ? "n" : ""} ${string}`;
}

/**
 * Convert a small number to the word, e.g. providing 1 will return "one", providing 22 will return "22"
 */
export function humanizeSmallNumber(number: number): string {
    if(number >= 10 || number <= 0) {
        return number.toString();
    }

    return [
        "one",
        "two",
        "three",
        "four",
        "five",
        "six",
        "seven",
        "eight",
        "nine",
    ][number - 1];
}

/**
 * Parse a string into another value
 */
export function parseString(
    value: string
): string | boolean | number | null | Record<string, unknown> | Array<unknown> {
    if(value === "true") {
        return true;
    } else if(value === "false") {
        return false;
    } else if(value === "null") {
        return null;
    } else {
        // Check if the value is a number
        const n = Number(value);
        const p = parseFloat(value);

        if(!isNaN(n) && !isNaN(p)) {
            return p;
        } else {
            try {
                return JSON.parse(value);
            } catch {
                // Could not parse, do nothing
            }
        }
    }

    return value;
}

const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

function getDateFormat(): string {
    return getOption("date_format") || DEFAULT_DATE_FORMAT;
}

/**
 * Formats a date object
 */
export function formatDate(date: string | Date | Moment): string {
    const format = getDateFormat();

    const d = moment(date);

    if(!d.isValid()) {
        console.error("Invalid date value", date);

        if(typeof date === "string") {
            return date;
        }

        return "Invalid date";
    }

    return d.format(format);
}

const DEFAULT_TIME_FORMAT = "HH:mm:ss";

function getTimeFormat(): string {
    return getOption("time_format") || DEFAULT_TIME_FORMAT;
}

/**
 * Formats a time object
 */
export function formatTime(time: string | Date | Moment): string {
    const format = getTimeFormat();

    const t = moment(time);

    if(!t.isValid()) {
        console.error("Invalid time value", time);

        if(typeof time === "string") {
            return time;
        }

        return "Invalid Time";
    }

    return t.format(format);
}

/**
 * Formats a date time object
 */
export function formatDateTime(datetime: Moment | Date | string): string {
    const dt = moment(datetime);

    if(!dt.isValid()) {
        console.error("Invalid date time value", datetime);

        if(typeof datetime === "string") {
            return datetime;
        }

        return "Invalid Date Time";
    }

    return formatDate(datetime) + " " + formatTime(datetime);
}

/**
 * Get the "case" text
 */
export function caseText(): string {
    return getOption("product_case_text").toString();
}

/**
 * Get the "single" text
 */
export function singleText(): string {
    return getOption("product_single_text").toString();
}

/**
 * Split a string that uses PascalCase into an array (["Pascal" "Case"])
 */
export function splitPascalCase(str: string): Array<string> {
    const split = str.match(/[A-Z][a-z]+/g);

    if(!split) {
        return [ str ];
    }

    return split;
}

/**
 * Convert a string from camelCase to snake_case
 */
export function snakeCase(str: string): string {
    return str.split(/(?=[A-Z])/).join("_").toLowerCase();
}

/**
 * Convert a string from snake_case or kebab-case to camelCase
 */
export function camelCase(str: string): string {
    const split = str.split(/[_-]/g);

    for(let i = 1, l = split.length; i < l; i++) {
        split[i] = uppercaseFirst(split[i]);
    }

    return split.join("");
}

/**
 * Convert a string to lower case (preserving the existing type)
 */
export function lowerCase<S extends string>(str: S): Lowercase<S> {
    return str.toLowerCase() as Lowercase<S>;
}

/**
 * Convert a string to upper case (preserving the existing type)
 */
export function upperCase<S extends string>(str: S): Uppercase<S> {
    return str.toUpperCase() as Uppercase<S>;
}

/**
 * Pad a string with the padding
 * @param padding The padding to use
 * @param stringToPad The string to be padded
 * @param padLeft Whether to pad from the left or the right
 * @example
 * // Returns "00001234"
 * pad("00000000", "1234", true)
 * @example
 * // Returns "ab"
 * pad("  ", "abcdef")
 */
export function pad(padding: string, stringToPad: string, padLeft = false): string {
    if(padLeft) {
        return (padding + stringToPad).slice(-padding.length);
    } else {
        return (stringToPad + padding).substring(0, padding.length);
    }
}

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 * @param text The text to be rendered.
 * @param font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text: string, font: string): number {
    // re-use canvas object for better performance
    if(typeof getTextWidth.canvas === "undefined") {
        getTextWidth.canvas = document.createElement("canvas");
    }

    const canvas = getTextWidth.canvas;
    const context = canvas.getContext("2d") as CanvasRenderingContext2D;

    context.font = font;

    const metrics = context.measureText(text);

    return Math.round(metrics.width * 100) / 100;
}

getTextWidth.canvas = undefined as undefined | HTMLCanvasElement;

// The regex to use for email validation, if changing here, also change EmailType.php
const EMAIL_REGEX =
    // eslint-disable-next-line no-useless-escape
    /^(?!.*\.@)(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

/**
 * Tests if a string is a valid email
 */
export function isValidEmail(value: string): boolean {
    return EMAIL_REGEX.test(value);
}

const EMOJI_REGEX = /\p{Emoji_Presentation}/gu;

export function removeEmoji(value: string): string {
    return value.replaceAll(EMOJI_REGEX, "");
}

const ALPHANUMERICS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/**
 * Generates a random alphanumeric string of a certain length
 */
export function randomString(length: number): string {
    const results: Array<string> = [];

    for(let i = 0; i < length; i++) {
        const index = Math.round(Math.random() * (ALPHANUMERICS.length - 1));

        results.push(ALPHANUMERICS.charAt(index));
    }

    return results.join("");
}

// From: https://stackoverflow.com/a/32045167
// eslint-disable-next-line @stylistic/max-len
const PERIOD_REGEX = /^P(?!$)(\d+(?:\.\d+)?Y)?(\d+(?:\.\d+)?M)?(\d+(?:\.\d+)?W)?(\d+(?:\.\d+)?D)?(T(?=\d)(\d+(?:\.\d+)?H)?(\d+(?:\.\d+)?M)?(\d+(?:\.\d+)?S)?)?$/;

/**
 * Tests is a string is a valid ISO 8601 Period Format
 */
export function isValidPeriod(value: string): boolean {
    return PERIOD_REGEX.test(value);
}

/**
 * Parses an ISO 8601 Period Format string into a Moment JS Duration object
 */
export function parseDuration(value: string): Duration | null {
    const trimmed = value.trim();

    if(!trimmed) {
        return null;
    }

    if(!isValidPeriod(trimmed)) {
        return null;
    }

    const parsed = moment.duration(trimmed);

    if(parsed.isValid()) {
        return parsed;
    }

    return null;
}
